import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Zap, Settings, History, CheckCircle, XCircle, Clock, AlertTriangle, Eye, Trash2, MessageCircle, Edit3, MoreVertical } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { 
  getAutomations, 
  createAutomation, 
  toggleAutomationLive, 
  updateAutomation,
  deleteAutomation,
  getCurrentBusinessId,
  getAutomationTriggers,
  saveAutomationTriggers,
  getAutomationActions,
  saveAutomationActions,
  getAutomationConnections,
  saveWireConnections,
  createAutomationConnection,
  getAutomationExecutions,
  getExecutionSummary,
  getExecutionSteps
} from '../lib/automationsV2';
import { AutomationV2, TriggerNode as TriggerNodeType, ActionNode as ActionNodeType, AutomationExecutionV2, AutomationActionExecutionV2 } from '../types/automationsV2';

interface WireState {
  isDragging: boolean;
  startNodeId: string | null;
  startPosition: { x: number; y: number };
  currentPosition: { x: number; y: number };
}

interface WireConnection {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  fromPort: 'outflow';
  toPort: 'inflow';
}

// Connection Port Component
interface ConnectionPortProps {
  nodeId: string;
  type: 'inflow' | 'outflow';
  onStartWireDrag: (nodeId: string, position: { x: number; y: number }) => void;
  onWireDropped?: (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => void;
}

const ConnectionPort: React.FC<ConnectionPortProps> = ({ nodeId, type, onStartWireDrag, onWireDropped }) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only start wire drags from outflow ports
    if (type !== 'outflow') return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Get the canvas element to calculate relative position
    const canvasElement = e.currentTarget.closest('[data-canvas]') as HTMLElement;
    if (!canvasElement) return;
    
    const portRect = e.currentTarget.getBoundingClientRect();
    const canvasRect = canvasElement.getBoundingClientRect();
    
    // Calculate position relative to canvas
    const position = {
      x: portRect.left + portRect.width / 2 - canvasRect.left,
      y: portRect.top + portRect.height / 2 - canvasRect.top
    };
    
    console.log(`🔗 Starting wire drag from ${nodeId} (${type}) at position:`, position);
    onStartWireDrag(nodeId, position);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    // Only handle wire drops on inflow ports
    if (type !== 'inflow' || !onWireDropped) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    console.log(`🔗 Wire dropped on ${nodeId} (${type})`);
    onWireDropped(nodeId, type);
  };

  return (
    <div
      className={`absolute w-4 h-4 rounded-full border-2 shadow-lg hover:scale-110 transition-transform ${
        type === 'inflow' 
          ? 'bg-white border-gray-500 top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 cursor-pointer hover:bg-gray-50' 
          : 'bg-gray-500 border-white hover:bg-gray-600 bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 cursor-crosshair'
      }`}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      title={type === 'inflow' ? 'Input connection' : 'Output connection'}
      data-node-id={nodeId}
      data-port-type={type}
    />
  );
};

// Trigger Node Component
interface TriggerNodeComponentProps {
  trigger: TriggerNodeType;
  onMouseDown: (e: React.MouseEvent, triggerId: string) => void;
  onStartWireDrag: (nodeId: string, position: { x: number; y: number }) => void;
  onSettingsClick: (triggerId: string) => void;
  onWireDropped: (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => void;
}

const TriggerNode: React.FC<TriggerNodeComponentProps> = ({ 
  trigger, 
  onMouseDown, 
  onStartWireDrag, 
  onSettingsClick,
  onWireDropped
}) => {
  const formatEventType = (eventType: string) => {
    return eventType.charAt(0).toUpperCase() + eventType.slice(1);
  };

  return (
    <div
      className="absolute bg-white rounded-lg border-2 border-gray-300 shadow-lg p-4 min-w-[200px] select-none transition-shadow duration-200 cursor-grab hover:shadow-xl"
      style={{
        left: trigger.position.x,
        top: trigger.position.y,
        zIndex: 1
      }}
      onMouseDown={(e) => onMouseDown(e, trigger.id)}
    >
      {/* Settings Icon */}
      <button
        className="absolute top-2 right-2 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center justify-center transition-colors"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSettingsClick(trigger.id);
        }}
        title="Configure trigger"
      >
        <Settings className="h-3 w-3 text-gray-600" />
      </button>

      <div className="flex items-center space-x-3 pointer-events-none">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <Zap className="h-5 w-5 text-purple-600" />
        </div>
        <div>
          <h4 className="font-medium text-gray-900">{trigger.name}</h4>
          <p className="text-xs text-gray-500">{formatEventType(trigger.config?.eventType || 'created')}</p>
          {trigger.config?.uiLabel && (
            <p className="text-xs text-purple-600 font-medium mt-1 bg-purple-50 px-2 py-1 rounded-md">
              {trigger.config.uiLabel}
            </p>
          )}
        </div>
      </div>

      {/* Outflow Port */}
      <ConnectionPort
        nodeId={trigger.id}
        type="outflow"
        onStartWireDrag={onStartWireDrag}
        onWireDropped={onWireDropped}
      />
    </div>
  );
};

// Action Node Component
interface ActionNodeComponentProps {
  action: ActionNodeType;
  onMouseDown: (e: React.MouseEvent, actionId: string) => void;
  onStartWireDrag: (nodeId: string, position: { x: number; y: number }) => void;
  onSettingsClick: (actionId: string) => void;
  onWireDropped: (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => void;
}

const ActionNode: React.FC<ActionNodeComponentProps> = ({ 
  action, 
  onMouseDown, 
  onStartWireDrag, 
  onSettingsClick,
  onWireDropped
}) => {
  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'sms':
        return <MessageCircle className="h-5 w-5 text-green-600" />;
      case 'delay':
        return <Clock className="h-5 w-5 text-blue-600" />;
      default:
        return '⚡';
    }
  };

  return (
    <div
      className="absolute bg-white rounded-lg border-2 border-gray-300 shadow-lg p-4 min-w-[200px] select-none transition-shadow duration-200 cursor-grab hover:shadow-xl"
      style={{
        left: action.position.x,
        top: action.position.y,
        zIndex: 1
      }}
      onMouseDown={(e) => onMouseDown(e, action.id)}
    >
      {/* Settings Icon */}
      <button
        className="absolute top-2 right-2 w-6 h-6 bg-gray-100 hover:bg-gray-200 rounded-md flex items-center justify-center transition-colors"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSettingsClick(action.id);
        }}
        title="Configure action"
      >
        <Settings className="h-3 w-3 text-gray-600" />
      </button>

      {/* Inflow Port */}
      <ConnectionPort
        nodeId={action.id}
        type="inflow"
        onStartWireDrag={onStartWireDrag}
        onWireDropped={onWireDropped}
      />

      <div className="flex items-center space-x-3 pointer-events-none">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          {getActionIcon(action.type)}
        </div>
        <div>
          <h4 className="font-medium text-gray-900">{action.name}</h4>
          <p className="text-xs text-gray-500">Action</p>
          {action.config?.uiLabel && (
            <p className="text-xs text-green-600 font-medium mt-1 bg-green-50 px-2 py-1 rounded-md">
              {action.config.uiLabel}
            </p>
          )}
        </div>
      </div>

      {/* Outflow Port */}
      <ConnectionPort
        nodeId={action.id}
        type="outflow"
        onStartWireDrag={onStartWireDrag}
        onWireDropped={onWireDropped}
      />
    </div>
  );
};

const AutomationsV2 = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTriggerLibraryOpen, setIsTriggerLibraryOpen] = useState(false);
  const [isActionLibraryOpen, setIsActionLibraryOpen] = useState(false);
  const [isTriggerSettingsOpen, setIsTriggerSettingsOpen] = useState(false);
  const [selectedTriggerId, setSelectedTriggerId] = useState<string | null>(null);
  const [automationTitle, setAutomationTitle] = useState('');
  const [automations, setAutomations] = useState<AutomationV2[]>([]);
  const [selectedAutomation, setSelectedAutomation] = useState<AutomationV2 | null>(null);
  const [triggers, setTriggers] = useState<TriggerNodeType[]>([]);
  const [actions, setActions] = useState<ActionNodeType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [wireState, setWireState] = useState<WireState>({
    isDragging: false,
    startNodeId: null,
    startPosition: { x: 0, y: 0 },
    currentPosition: { x: 0, y: 0 }
  });
  
  // New state for execution history
  const [activeTab, setActiveTab] = useState<'workflow' | 'history'>('workflow');
  const [executions, setExecutions] = useState<AutomationExecutionV2[]>([]);
  const [executionSummary, setExecutionSummary] = useState<{
    total_executions: number;
    successful_executions: number;
    failed_executions: number;
    average_duration_ms: number;
    last_execution?: AutomationExecutionV2;
  } | null>(null);
  
  // Execution details modal state
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(null);
  const [executionSteps, setExecutionSteps] = useState<AutomationActionExecutionV2[]>([]);
  const [isExecutionDetailsOpen, setIsExecutionDetailsOpen] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState(false);

  // State for permanent wire connections
  const [wireConnections, setWireConnections] = useState<WireConnection[]>([]);
  
  // Flag to prevent debounced saves during immediate saves
  const [isSavingImmediately, setIsSavingImmediately] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);
  const wireRerenderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { selectedBusiness } = useBusiness();

  // Available trigger types
  const availableTriggers = [
    {
      id: 'booking',
      name: 'Booking',
      description: 'Triggers when a booking is created, updated, or cancelled',
      icon: <Zap className="h-6 w-6 text-purple-600" />,
      color: 'bg-blue-100 border-blue-300'
    }
  ];

  // Available action types
  const availableActions = [
    {
      id: 'sms',
      name: 'SMS',
      description: 'Send an SMS message to customers or team members',
      icon: <MessageCircle className="h-6 w-6 text-green-600" />,
      color: 'bg-green-100 border-green-300'
    },
    {
      id: 'delay',
      name: 'Wait',
      description: 'Pause the automation for a specified amount of time',
      icon: <Clock className="h-6 w-6 text-blue-600" />,
      color: 'bg-blue-100 border-blue-300'
    }
  ];

  // New state for trigger settings
  const [triggerSettingsModal, setTriggerSettingsModal] = useState<{
    isOpen: boolean;
    triggerId: string | null;
    triggerType: string;
    eventType: string;
  }>({
    isOpen: false,
    triggerId: null,
    triggerType: '',
    eventType: 'created'
  });

  // New state for SMS action settings
  const [smsActionSettingsModal, setSmsActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    message: string;
    recipient: string;
    customPhone: string;
  }>({
    isOpen: false,
    actionId: null,
    message: '',
    recipient: '',
    customPhone: ''
  });

  // New state for Delay action settings
  const [delayActionSettingsModal, setDelayActionSettingsModal] = useState<{
    isOpen: boolean;
    actionId: string | null;
    waitType: 'duration' | 'reminder';
    duration: number;
    unit: 'minutes' | 'hours' | 'days';
    reminderHoursBefore: number;
    reminderReference: 'drop_off_datetime' | 'start_datetime';
  }>({
    isOpen: false,
    actionId: null,
    waitType: 'duration',
    duration: 1,
    unit: 'hours',
    reminderHoursBefore: 24,
    reminderReference: 'drop_off_datetime'
  });

  // State for edit automation title modal
  const [isEditTitleModalOpen, setIsEditTitleModalOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  
  // State for delete automation modal
  const [isDeleteAutomationModalOpen, setIsDeleteAutomationModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Reference for SMS message textarea to insert variables
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Load automations when component mounts or business changes
  useEffect(() => {
    if (selectedBusiness?.id) {
      loadAutomations();
    }
  }, [selectedBusiness?.id]);

  // Load triggers when automation changes
  useEffect(() => {
    if (selectedAutomation?.id) {
      loadTriggers();
      loadActions();
      loadWireConnections();
      if (activeTab === 'history') {
        loadExecutionHistory();
      }
    } else {
      setTriggers([]);
      setActions([]);
      setExecutions([]);
      setExecutionSummary(null);
    }
  }, [selectedAutomation?.id, activeTab]);

  // ResizeObserver to detect when nodes finish layout changes
  useEffect(() => {
    if (!canvasRef.current || triggers.length === 0 && actions.length === 0) return;

    const observer = new ResizeObserver((entries) => {
      // Debounce the wire re-render to avoid excessive updates
      if (wireRerenderTimeoutRef.current) {
        clearTimeout(wireRerenderTimeoutRef.current);
      }
      wireRerenderTimeoutRef.current = setTimeout(() => {
        console.log('📐 ResizeObserver detected layout changes - updating wires');
        setWireConnections(prev => [...prev]);
      }, 100);
    });

    // Observe all node elements for layout changes
    const nodeElements = canvasRef.current.querySelectorAll('[data-node-id]');
    nodeElements.forEach(element => observer.observe(element));

    return () => {
      observer.disconnect();
      if (wireRerenderTimeoutRef.current) {
        clearTimeout(wireRerenderTimeoutRef.current);
      }
    };
  }, [triggers.length, actions.length]);

  // Multiple trigger approach for wire positioning
  useEffect(() => {
    if (triggers.length > 0 || actions.length > 0) {
      // Immediate attempt (for cases where DOM is ready)
      setTimeout(() => {
        console.log('🔗 Immediate wire positioning attempt');
        setWireConnections(prev => [...prev]);
      }, 50);

      // Delayed attempt (for slower rendering)
      setTimeout(() => {
        console.log('🔗 Delayed wire positioning attempt');
        setWireConnections(prev => [...prev]);
      }, 300);

      // Final attempt (fallback)
      setTimeout(() => {
        console.log('🔗 Final wire positioning attempt');
        setWireConnections(prev => [...prev]);
      }, 800);
    }
  }, [triggers.length, actions.length]);

  // Also re-render when wire connections are first loaded
  useEffect(() => {
    if (wireConnections.length > 0) {
      setTimeout(() => {
        console.log('🔗 Wire connections loaded - positioning update');
        setWireConnections(prev => [...prev]);
      }, 100);
    }
  }, [wireConnections.length]);

  // Save triggers to database when triggers change (with debounce)
  useEffect(() => {
    if (selectedAutomation?.id && triggers.length >= 0) {
      const timeoutId = setTimeout(() => {
        saveTriggers();
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [triggers, selectedAutomation?.id]);

  // Save actions to database when actions change (with debounce)
  useEffect(() => {
    if (selectedAutomation?.id && actions.length >= 0) {
      const timeoutId = setTimeout(() => {
        saveActions();
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [actions, selectedAutomation?.id]);

  // Save wire connections to database when connections change (with debounce)
  useEffect(() => {
    if (selectedAutomation?.id && wireConnections.length >= 0 && !isSavingImmediately) {
      const timeoutId = setTimeout(() => {
        saveConnections();
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [wireConnections, selectedAutomation?.id, isSavingImmediately]);

  // Handle mouse events for node dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !draggedId || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const newX = Math.max(0, e.clientX - rect.left - dragOffset.x);
      const newY = Math.max(0, e.clientY - rect.top - dragOffset.y);

      // Update trigger positions
      setTriggers(prev => prev.map(trigger => 
        trigger.id === draggedId 
          ? { ...trigger, position: { x: newX, y: newY } }
          : trigger
      ));

      // Update action positions
      setActions(prev => prev.map(action => 
        action.id === draggedId 
          ? { ...action, position: { x: newX, y: newY } }
          : action
      ));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setDraggedId(null);
      setDragOffset({ x: 0, y: 0 });
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, draggedId, dragOffset]);

  // Handle mouse events for wire dragging
  useEffect(() => {
    const handleWireMouseMove = (e: MouseEvent) => {
      if (!wireState.isDragging || !canvasRef.current) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      setWireState(prev => ({
        ...prev,
        currentPosition: { x: currentX, y: currentY }
      }));
    };

    const handleWireMouseUp = () => {
      if (wireState.isDragging) {
        console.log('🔗 Wire drag ended - from:', wireState.startPosition, 'to:', wireState.currentPosition);
        setWireState({
          isDragging: false,
          startNodeId: null,
          startPosition: { x: 0, y: 0 },
          currentPosition: { x: 0, y: 0 }
        });
      }
    };

    if (wireState.isDragging) {
      document.addEventListener('mousemove', handleWireMouseMove);
      document.addEventListener('mouseup', handleWireMouseUp);
      document.body.style.cursor = 'crosshair';
      console.log('🔗 Wire dragging active - from node:', wireState.startNodeId);
    }

    return () => {
      document.removeEventListener('mousemove', handleWireMouseMove);
      document.removeEventListener('mouseup', handleWireMouseUp);
      if (wireState.isDragging) {
        document.body.style.cursor = '';
      }
    };
  }, [wireState.isDragging]);

  // Debug wire state changes
  useEffect(() => {
    console.log('🔗 WireState changed:', wireState);
    if (wireState.isDragging) {
      console.log('🔗 Wire should be visible now! Position from', wireState.startPosition, 'to', wireState.currentPosition);
    }
  }, [wireState]);

  const loadAutomations = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getAutomations(selectedBusiness.id);
      setAutomations(data);
    } catch (err) {
      console.error('Error loading automations:', err);
      setError('Failed to load automations');
    } finally {
      setLoading(false);
    }
  };

  const loadTriggers = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      const data = await getAutomationTriggers(selectedAutomation.id);
      setTriggers(data);
    } catch (err) {
      console.error('Error loading triggers:', err);
      setError('Failed to load triggers');
    }
  };

  const loadActions = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      const data = await getAutomationActions(selectedAutomation.id);
      setActions(data);
    } catch (err) {
      console.error('Error loading actions:', err);
      setError('Failed to load actions');
    }
  };

  const loadExecutionHistory = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      const [executionsData, summaryData] = await Promise.all([
        getAutomationExecutions(selectedAutomation.id),
        getExecutionSummary(selectedAutomation.id)
      ]);
      
      setExecutions(executionsData);
      setExecutionSummary(summaryData);
    } catch (err) {
      console.error('Error loading execution history:', err);
      setError('Failed to load execution history');
    }
  };

  const saveTriggers = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      await saveAutomationTriggers(selectedAutomation.id, triggers);
    } catch (err) {
      console.error('Error saving triggers:', err);
      setError('Failed to save triggers');
    }
  };

  const saveActions = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      await saveAutomationActions(selectedAutomation.id, actions);
    } catch (err) {
      console.error('Error saving actions:', err);
      setError('Failed to save actions');
    }
  };

  const saveConnections = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      await saveWireConnections(selectedAutomation.id, wireConnections);
      console.log('💾 Saved wire connections to database:', wireConnections.length);
    } catch (err) {
      console.error('Error saving wire connections:', err);
      setError('Failed to save wire connections');
    }
  };

  const loadExecutionSteps = async (executionId: number) => {
    try {
      setLoadingSteps(true);
      const steps = await getExecutionSteps(executionId);
      setExecutionSteps(steps);
    } catch (err) {
      console.error('Error loading execution steps:', err);
      setError('Failed to load execution details');
    } finally {
      setLoadingSteps(false);
    }
  };

  const handleViewExecution = async (executionId: number) => {
    setSelectedExecutionId(executionId.toString());
    setIsExecutionDetailsOpen(true);
    await loadExecutionSteps(executionId);
  };

  const handleCloseExecutionDetails = () => {
    setIsExecutionDetailsOpen(false);
    setSelectedExecutionId(null);
    setExecutionSteps([]);
  };

  // Helper function to format duration
  const formatDuration = (durationMs: number): string => {
    if (durationMs < 1000) return `${Math.round(durationMs)}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
    return `${(durationMs / 60000).toFixed(1)}m`;
  };

  // Helper function to format relative time
  const formatRelativeTime = (dateString: string): string => {
    const now = new Date().getTime();
    const date = new Date(dateString).getTime();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  // Helper function to get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'running':
        return <Clock className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'pending':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const handleCreateClick = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setAutomationTitle('');
    setError(null);
  };

  const handleSaveAutomation = async () => {
    if (!automationTitle.trim() || !selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const newAutomation = await createAutomation({
        name: automationTitle.trim(),
        business_id: selectedBusiness.id
      });
      
      setAutomations(prev => [newAutomation, ...prev]);
      console.log('Created automation:', newAutomation);
      handleCloseModal();
    } catch (err) {
      console.error('Error creating automation:', err);
      setError('Failed to create automation');
    } finally {
      setLoading(false);
    }
  };

  const handleAutomationClick = (automation: AutomationV2) => {
    setSelectedAutomation(automation);
    setActiveTab('workflow'); // Reset to workflow tab when changing automation
  };

  const handleToggleLive = async () => {
    if (!selectedAutomation) return;

    try {
      setError(null);
      const updatedAutomation = await toggleAutomationLive(
        selectedAutomation.id, 
        !selectedAutomation.is_live
      );

      setAutomations(prev => 
        prev.map(automation => 
          automation.id === selectedAutomation.id ? updatedAutomation : automation
        )
      );
      setSelectedAutomation(updatedAutomation);
    } catch (err) {
      console.error('Error toggling automation live status:', err);
      setError('Failed to update automation status');
    }
  };

  const handleAddTrigger = () => {
    setIsTriggerLibraryOpen(true);
  };

  const handleSelectTrigger = (triggerType: any) => {
    const newTrigger: TriggerNodeType = {
      id: `temp-${Date.now()}`,
      type: triggerType.id,
      name: triggerType.name,
      position: { x: 100, y: 100 },
      config: { eventType: 'created' }
    };

    setTriggers(prev => [...prev, newTrigger]);
    setIsTriggerLibraryOpen(false);
    console.log('Added trigger:', newTrigger);
  };

  const handleMouseDown = (e: React.MouseEvent, triggerId: string) => {
    e.preventDefault();
    
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const trigger = triggers.find(t => t.id === triggerId);
    
    if (!trigger) return;

    const offsetX = e.clientX - rect.left - trigger.position.x;
    const offsetY = e.clientY - rect.top - trigger.position.y;

    setIsDragging(true);
    setDraggedId(triggerId);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const handleStartWireDrag = (nodeId: string, position: { x: number; y: number }) => {
    console.log(`🔗 Starting wire drag from node: ${nodeId} at position:`, position);
    setWireState({
      isDragging: true,
      startNodeId: nodeId,
      startPosition: position,
      currentPosition: position
    });
  };

  // Handle successful wire connection
  const handleWireDropped = async (targetNodeId: string, targetPortType: 'inflow' | 'outflow') => {
    if (!wireState.isDragging || !wireState.startNodeId) return;
    
    // Prevent connecting to the same node
    if (wireState.startNodeId === targetNodeId) {
      console.log('🔗 Cannot connect node to itself');
      setWireState({
        isDragging: false,
        startNodeId: null,
        startPosition: { x: 0, y: 0 },
        currentPosition: { x: 0, y: 0 }
      });
      return;
    }
    
    // Check if connection already exists
    const existingConnection = wireConnections.find(
      conn => conn.fromNodeId === wireState.startNodeId && conn.toNodeId === targetNodeId
    );
    
    if (existingConnection) {
      console.log('🔗 Connection already exists');
      setWireState({
        isDragging: false,
        startNodeId: null,
        startPosition: { x: 0, y: 0 },
        currentPosition: { x: 0, y: 0 }
      });
      return;
    }
    
    try {
      // Set flag to prevent debounced saves
      setIsSavingImmediately(true);
      
      // Ensure all nodes are saved to database before creating connection
      console.log('🔗 Ensuring nodes are saved before creating connection...');
      if (selectedAutomation?.id) {
        await Promise.all([
          saveAutomationTriggers(selectedAutomation.id, triggers),
          saveAutomationActions(selectedAutomation.id, actions)
        ]);
        
        // Reload nodes to get database UUIDs (in case new nodes were created)
        const [updatedTriggers, updatedActions] = await Promise.all([
          getAutomationTriggers(selectedAutomation.id),
          getAutomationActions(selectedAutomation.id)
        ]);
        
        // Update local state with database UUIDs
        setTriggers(updatedTriggers);
        setActions(updatedActions);
        
        console.log('🔗 Nodes saved and reloaded with database UUIDs');
        
        // Map temporary IDs to database UUIDs
        const findActualNodeId = (tempId: string): string => {
          // Check if it's already a UUID
          if (tempId.length === 36 && tempId.includes('-')) {
            return tempId;
          }
          
          // Find in updated triggers
          const trigger = updatedTriggers.find(t => t.name === triggers.find(orig => orig.id === tempId)?.name);
          if (trigger) return trigger.id;
          
          // Find in updated actions  
          const action = updatedActions.find(a => a.name === actions.find(orig => orig.id === tempId)?.name);
          if (action) return action.id;
          
          return tempId; // Fallback to original ID
        };
        
        // Update the connection IDs to use database UUIDs
        const actualFromNodeId = findActualNodeId(wireState.startNodeId);
        const actualToNodeId = findActualNodeId(targetNodeId);
        
        console.log('🔗 Mapped IDs - From:', wireState.startNodeId, '→', actualFromNodeId, 'To:', targetNodeId, '→', actualToNodeId);
        
        // Update connection with actual database IDs
        const newConnection: WireConnection = {
          id: `wire-${Date.now()}`,
          fromNodeId: actualFromNodeId,
          toNodeId: actualToNodeId,
          fromPort: 'outflow',
          toPort: 'inflow'
        };
        
        setWireConnections(prev => [...prev, newConnection]);
        console.log('🔗 Created wire connection with database UUIDs:', newConnection);
        
        // Save connection to database with actual UUIDs
        await createAutomationConnection({
          automation_id: selectedAutomation.id,
          source_node_id: actualFromNodeId,
          target_node_id: actualToNodeId
        });
        console.log('🔗 Connection saved to database successfully');
      }
      
      // Clear the flag after a short delay to allow debounced saves to resume
      setTimeout(() => {
        setIsSavingImmediately(false);
      }, 1000);
      
      

    } catch (err) {
      console.error('Error creating connection:', err);
      setError('Failed to save wire connection');
      // Clear flag even on error
      setIsSavingImmediately(false);
    }
    
    // Clear wire drag state
    setWireState({
      isDragging: false,
      startNodeId: null,
      startPosition: { x: 0, y: 0 },
      currentPosition: { x: 0, y: 0 }
    });
  };

  const handleTriggerSettings = (triggerId: string) => {
    setSelectedTriggerId(triggerId);
    setIsTriggerSettingsOpen(true);
  };

  const handleCloseTriggerSettings = () => {
    setIsTriggerSettingsOpen(false);
    setSelectedTriggerId(null);
  };

  const handleDeleteTrigger = (triggerId: string) => {
    // Remove the trigger from the triggers array
    setTriggers(prev => prev.filter(trigger => trigger.id !== triggerId));
    
    // Remove any wire connections involving this trigger
    setWireConnections(prev => prev.filter(connection => 
      connection.fromNodeId !== triggerId && connection.toNodeId !== triggerId
    ));
    
    // Close the settings modal
    handleCloseTriggerSettings();
    
    console.log('Trigger and related connections deleted:', triggerId);
  };

  // Action handlers
  const handleAddAction = () => {
    setIsActionLibraryOpen(true);
  };

  const handleSelectAction = (actionType: any) => {
    const newAction: ActionNodeType = {
      id: `action-${Date.now()}`,
      type: actionType.id,
      name: actionType.name,
      position: { x: 400, y: 100 }, // Position to the right of triggers
      config: {}
    };

    setActions(prev => [...prev, newAction]);
    setIsActionLibraryOpen(false);
    console.log('Added action:', newAction);
  };

  const handleActionMouseDown = (e: React.MouseEvent, actionId: string) => {
    e.preventDefault();
    
    if (!canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const action = actions.find(a => a.id === actionId);
    
    if (!action) return;

    const offsetX = e.clientX - rect.left - action.position.x;
    const offsetY = e.clientY - rect.top - action.position.y;

    setIsDragging(true);
    setDraggedId(actionId);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  // Handle opening action settings
  const handleActionSettingsClick = (actionId: string) => {
    const currentAction = actions.find(action => action.id === actionId);
    
    if (currentAction?.type === 'sms') {
      // SMS Action Settings
      const existingMessage = currentAction?.config?.message || '';
      const existingRecipient = currentAction?.config?.recipient || '';
      const existingCustomPhone = currentAction?.config?.customPhone || '';
      
      setSmsActionSettingsModal({
        isOpen: true,
        actionId,
        message: existingMessage,
        recipient: existingRecipient,
        customPhone: existingCustomPhone
      });
    } else if (currentAction?.type === 'delay') {
      // Delay Action Settings
      const existingDuration = currentAction?.config?.displayDuration || currentAction?.config?.duration || 1;
      const existingUnit = currentAction?.config?.unit || 'hours';
      const existingWaitType = currentAction?.config?.wait_type || 'duration';
      const existingReminderHours = currentAction?.config?.reminder_hours_before || 24;
      const existingReminderReference = currentAction?.config?.reminder_reference || 'drop_off_datetime';
      
      setDelayActionSettingsModal({
        isOpen: true,
        actionId,
        waitType: existingWaitType,
        duration: existingDuration,
        unit: existingUnit,
        reminderHoursBefore: existingReminderHours,
        reminderReference: existingReminderReference
      });
    }
  };

  // Helper function to check if wait action is connected to a "created" trigger
  const isConnectedToCreatedTrigger = (actionId: string): boolean => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    
    // Check if any connected trigger is a booking trigger with "created" event type
    return connectedTriggers.some(trigger => 
      trigger?.type === 'booking' && trigger.config?.eventType === 'created'
    );
  };

  // Handle deleting action from settings modal
  const handleDeleteActionFromSettings = async () => {
    if (!smsActionSettingsModal.actionId) return;

    try {
      const actionId = smsActionSettingsModal.actionId;
      
      // Remove action from local state
      setActions(prev => prev.filter(action => action.id !== actionId));
      
      // Remove any wire connections involving this action
      setWireConnections(prev => prev.filter(connection => 
        connection.fromNodeId !== actionId && connection.toNodeId !== actionId
      ));
      
      // Close modal
      setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' });
      
      console.log('Action and related connections deleted:', actionId);
    } catch (error) {
      console.error('Error deleting action:', error);
    }
  };

  // Get available notification recipients based on connected triggers/actions (with chain traversal)
  const getAvailableRecipients = (actionId: string): Array<{value: string, label: string}> => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    const recipients: Array<{value: string, label: string}> = [];
    
    // Add recipients based on connected trigger types
    connectedTriggers.forEach(trigger => {
      if (trigger?.type === 'booking') {
        if (!recipients.some(r => r.value === 'customer_phone')) {
          recipients.push({ value: 'customer_phone', label: 'Customer Phone' });
        }
        if (!recipients.some(r => r.value === 'resource_phone')) {
          recipients.push({ value: 'resource_phone', label: 'Resource Phone' });
        }
        // Note: Business Phone removed for booking triggers - not typically needed for booking notifications
      }
    });
    
    // Always allow custom phone number option
    recipients.push({ value: 'custom', label: 'Custom Phone Number' });
    
    return recipients;
  };

  // Get available variables based on connected triggers/actions (with chain traversal)
  const getAvailableVariables = (actionId: string): Array<{value: string, label: string, description: string}> => {
    // Traverse the connection chain backwards to find all triggers
    const findTriggersInChain = (nodeId: string, visited = new Set<string>()): TriggerNodeType[] => {
      if (visited.has(nodeId)) return []; // Prevent infinite loops
      visited.add(nodeId);
      
      // Check if this node is a trigger
      const directTrigger = triggers.find(trigger => trigger.id === nodeId);
      if (directTrigger) return [directTrigger];
      
      // Find all nodes that connect TO this node
      const incomingConnections = wireConnections.filter(conn => conn.toNodeId === nodeId);
      let foundTriggers: TriggerNodeType[] = [];
      
      // Recursively check each incoming connection
      for (const connection of incomingConnections) {
        const triggersFromUpstream = findTriggersInChain(connection.fromNodeId, visited);
        foundTriggers = [...foundTriggers, ...triggersFromUpstream];
      }
      
      return foundTriggers;
    };
    
    const connectedTriggers = findTriggersInChain(actionId);
    const variables: Array<{value: string, label: string, description: string}> = [];
    
    // Add variables based on connected trigger types
    connectedTriggers.forEach(trigger => {
      if (trigger?.type === 'booking') {
        // Customer variables
        variables.push(
          { value: '{customer_name}', label: 'Customer Name', description: 'Full name of the customer' },
          { value: '{first_name}', label: 'First Name', description: 'Customer\'s first name' },
          { value: '{last_name}', label: 'Last Name', description: 'Customer\'s last name' },
          { value: '{customer_phone}', label: 'Customer Phone', description: 'Customer\'s phone number' },
          { value: '{customer_email}', label: 'Customer Email', description: 'Customer\'s email address' }
        );
        
        // Booking variables
        variables.push(
          { value: '{booking_number}', label: 'Booking Number', description: 'Unique booking number' },
          { value: '{booking_title}', label: 'Booking Title', description: 'Title/name of the booking' },
          { value: '{grand_total}', label: 'Grand Total', description: 'Total cost of the booking' }
        );
        
        // Schedule variables
        variables.push(
          { value: '{start_date}', label: 'Start Date', description: 'Booking start date' },
          { value: '{start_time}', label: 'Start Time', description: 'Booking start time' },
          { value: '{drop_off_date}', label: 'Drop Off Date', description: 'Drop off date' },
          { value: '{drop_off_time}', label: 'Drop Off Time', description: 'Drop off time' }
        );
        
        // Vehicle variables
        variables.push(
          { value: '{vehicle_make}', label: 'Vehicle Make', description: 'Make of the customer\'s vehicle (e.g., Toyota)' },
          { value: '{vehicle_model}', label: 'Vehicle Model', description: 'Model of the customer\'s vehicle (e.g., Camry)' },
          { value: '{vehicle_year}', label: 'Vehicle Year', description: 'Year of the customer\'s vehicle' },
          { value: '{vehicle_color}', label: 'Vehicle Color', description: 'Color of the customer\'s vehicle' },
          { value: '{vehicle_info}', label: 'Vehicle Info', description: 'Full vehicle information (Year Make Model)' }
        );
        
        // Payment variables - only for payment-related triggers
        if (trigger.config?.eventType === 'partially_paid' || trigger.config?.eventType === 'paid') {
          variables.push(
            { value: '{invoice_link}', label: 'Invoice Link', description: 'Link to view the most recent invoice/receipt (only available for payment triggers)' }
          );
        }
        
        // Feedback link variable - only for completed booking triggers
        if (trigger.config?.eventType === 'completed') {
          variables.push(
            { value: '{feedback_link}', label: 'Feedback Link', description: 'Link for customer to leave feedback (only available for completed bookings)' }
          );
        }
      }
    });
    
    return variables;
  };

  // Insert variable into SMS message at cursor position
  const insertVariable = (variable: string) => {
    if (!messageTextareaRef.current) return;
    
    const textarea = messageTextareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentMessage = smsActionSettingsModal.message;
    
    // Insert the variable at cursor position
    const newMessage = currentMessage.substring(0, start) + variable + currentMessage.substring(end);
    
    // Update the message
    setSmsActionSettingsModal(prev => ({ ...prev, message: newMessage }));
    
    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  // Handle saving SMS message
  const handleSaveSmsMessage = () => {
    if (!smsActionSettingsModal.actionId) return;

    // Update the action with the new message and recipient
    setActions(prev => prev.map(action => 
      action.id === smsActionSettingsModal.actionId
        ? { 
            ...action, 
            config: { 
              ...action.config, 
              message: smsActionSettingsModal.message,
              recipient: smsActionSettingsModal.recipient,
              customPhone: smsActionSettingsModal.customPhone
            }
          }
        : action
    ));

    // Close modal
    setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' });
  };

  // Get node position for wire rendering - using DOM-based positioning like drag wires
  const getNodePosition = (nodeId: string, portType: 'inflow' | 'outflow') => {
    if (!canvasRef.current) return { x: 0, y: 0 };

    // Find the actual DOM element for the port
    const portSelector = portType === 'inflow' 
      ? `[data-node-id="${nodeId}"][data-port-type="inflow"]`
      : `[data-node-id="${nodeId}"][data-port-type="outflow"]`;
    
    const portElement = canvasRef.current.querySelector(portSelector) as HTMLElement;
    
    if (portElement) {
      // Use the same calculation as drag wires - get actual DOM position
      const portRect = portElement.getBoundingClientRect();
      const canvasRect = canvasRef.current.getBoundingClientRect();
      
      return {
        x: portRect.left + portRect.width / 2 - canvasRect.left,
        y: portRect.top + portRect.height / 2 - canvasRect.top
      };
    }

    // Improved fallback calculation when DOM elements aren't available yet
    const trigger = triggers.find(t => t.id === nodeId);
    if (trigger) {
      // More precise calculation accounting for actual CSS structure:
      // p-4 = 32px padding, icon+text ≈ 48px, label = mt-1(4px) + py-1(8px) + text(16px) = 28px
      const basePadding = 32; // p-4 top+bottom padding  
      const contentHeight = 48; // Icon + name + type text
      const labelHeight = trigger.config?.uiLabel ? 32 : 0; // mt-1 + py-1 + px-2 + text + bg
      const totalHeight = basePadding + contentHeight + labelHeight;
      
      return {
        x: trigger.position.x + 100, // Center of node (min-w-[200px] / 2)
        y: trigger.position.y + totalHeight + 8 // Bottom + port radius
      };
    }
    
    const action = actions.find(a => a.id === nodeId);
    if (action) {
      // Same precise calculation for actions
      const basePadding = 32; // p-4 top+bottom padding
      const contentHeight = 48; // Icon + name + type text  
      const labelHeight = action.config?.uiLabel ? 32 : 0; // mt-1 + py-1 + px-2 + text + bg
      const totalHeight = basePadding + contentHeight + labelHeight;
      
      if (portType === 'inflow') {
        return {
          x: action.position.x + 100, // Center of node (min-w-[200px] / 2)
          y: action.position.y - 8    // Top - port radius
        };
      } else {
        return {
          x: action.position.x + 100, // Center of node (min-w-[200px] / 2)
          y: action.position.y + totalHeight + 8 // Bottom + port radius
        };
      }
    }
    
    return { x: 0, y: 0 };
  };

  const selectedTrigger = selectedTriggerId ? triggers.find(t => t.id === selectedTriggerId) : null;

  const loadWireConnections = async () => {
    if (!selectedAutomation?.id) return;
    
    try {
      const data = await getAutomationConnections(selectedAutomation.id);
      setWireConnections(data);
    } catch (err) {
      console.error('Error loading wire connections:', err);
      setError('Failed to load wire connections');
    }
  };

  // Handle edit automation title
  const handleEditTitle = () => {
    if (selectedAutomation) {
      setEditTitle(selectedAutomation.name);
      setIsEditTitleModalOpen(true);
    }
  };

  const handleSaveTitle = async () => {
    if (!selectedAutomation?.id || !editTitle.trim()) return;

    try {
      setLoading(true);
      setError(null);

      const updatedAutomation = await updateAutomation(selectedAutomation.id, {
        name: editTitle.trim()
      });

      // Update automations list
      setAutomations(prev => 
        prev.map(automation => 
          automation.id === selectedAutomation.id ? updatedAutomation : automation
        )
      );

      // Update selected automation
      setSelectedAutomation(updatedAutomation);
      setIsEditTitleModalOpen(false);
      setEditTitle('');
    } catch (err) {
      console.error('Error updating automation title:', err);
      setError('Failed to update automation title');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete automation
  const handleDeleteAutomation = () => {
    setIsDeleteAutomationModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAutomation?.id) return;

    try {
      setDeleteLoading(true);
      setError(null);

      await deleteAutomation(selectedAutomation.id);

      // Remove from automations list
      setAutomations(prev => prev.filter(automation => automation.id !== selectedAutomation.id));
      
      // Clear selected automation
      setSelectedAutomation(null);
      setIsDeleteAutomationModalOpen(false);
    } catch (err) {
      console.error('Error deleting automation:', err);
      setError('Failed to delete automation');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Automations</h1>
          <button
            onClick={handleCreateClick}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create
          </button>
        </div>

        {/* Automation List */}
        <div className="flex-1 overflow-y-auto">
          {loading && automations.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Loading automations...
            </div>
          ) : automations.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No automations yet.
              <br />
              Create your first automation to get started.
            </div>
          ) : (
            <div className="p-4 space-y-2">
              {automations.map((automation) => (
                <div
                  key={automation.id}
                  onClick={() => handleAutomationClick(automation)}
                  className={`p-4 border rounded-lg cursor-pointer transition-all ${
                    selectedAutomation?.id === automation.id
                      ? 'bg-purple-50 border-purple-200'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {automation.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        automation.is_live 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {automation.is_live ? 'Live' : 'Draft'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {selectedAutomation ? (
          <>
            {/* Header with automation info and tabs */}
            <div className="bg-white border-b border-gray-200">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-gray-900">
                          {selectedAutomation.name}
                        </h2>
                        <button
                          onClick={handleEditTitle}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="Edit title"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                      </div>

                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleToggleLive}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedAutomation.is_live
                          ? 'bg-red-100 hover:bg-red-200 text-red-700'
                          : 'bg-green-100 hover:bg-green-200 text-green-700'
                      }`}
                    >
                      {selectedAutomation.is_live ? 'Turn Off' : 'Go Live'}
                    </button>
                    <button
                      onClick={handleDeleteAutomation}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete automation"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('workflow')}
                  className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === 'workflow'
                      ? 'border-gray-500 text-gray-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Zap className="h-4 w-4 inline mr-2" />
                  Workflow
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                    activeTab === 'history'
                      ? 'border-gray-500 text-gray-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <History className="h-4 w-4 inline mr-2" />
                  History
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'workflow' ? (
              /* Workflow Canvas */
              <div className="flex-1 relative">
                {/* Canvas Controls */}
                <div className="absolute top-4 left-4 z-10">
                  <button
                    onClick={handleAddTrigger}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 shadow-lg"
                  >
                    <Plus className="h-4 w-4" />
                    Add Trigger
                  </button>
                </div>

                <div className="absolute top-4 left-44 z-10">
                  <button
                    onClick={handleAddAction}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 shadow-lg"
                  >
                    <Plus className="h-4 w-4" />
                    Add Action
                  </button>
                </div>

                {/* Dotted Canvas */}
                <div 
                  ref={canvasRef}
                  data-canvas
                  className="w-full h-full relative overflow-hidden"
                  style={{
                    backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                >
                  {/* Trigger Nodes */}
                  {triggers.map((trigger) => (
                    <TriggerNode
                      key={trigger.id}
                      trigger={trigger}
                      onMouseDown={handleMouseDown}
                      onStartWireDrag={handleStartWireDrag}
                      onSettingsClick={handleTriggerSettings}
                      onWireDropped={handleWireDropped}
                    />
                  ))}

                  {/* Action Nodes */}
                  {actions.map((action) => (
                    <ActionNode
                      key={action.id}
                      action={action}
                      onMouseDown={handleActionMouseDown}
                      onStartWireDrag={handleStartWireDrag}
                      onSettingsClick={handleActionSettingsClick}
                      onWireDropped={handleWireDropped}
                    />
                  ))}

                  {/* Wire being dragged */}
                  {wireState.isDragging && (
                    <svg 
                      className="absolute inset-0 pointer-events-none" 
                      style={{ 
                        zIndex: 1000,
                        width: '100%',
                        height: '100%'
                      }}
                    >
                      <defs>
                        <marker
                          id="arrowhead"
                          markerWidth="10"
                          markerHeight="7"
                          refX="9"
                          refY="3.5"
                          orient="auto"
                          fill="#6b7280"
                        >
                          <polygon points="0 0, 10 3.5, 0 7" />
                        </marker>
                      </defs>
                      
                      {/* Connection wire */}
                      <line
                        x1={wireState.startPosition.x}
                        y1={wireState.startPosition.y}
                        x2={wireState.currentPosition.x || wireState.startPosition.x}
                        y2={wireState.currentPosition.y || wireState.startPosition.y}
                        stroke="#6b7280"
                        strokeWidth="2"
                        strokeDasharray="5,5"
                        opacity="0.8"
                      />
                      
                      {/* Start point indicator */}
                      <circle
                        cx={wireState.startPosition.x}
                        cy={wireState.startPosition.y}
                        r="4"
                        fill="#6b7280"
                        opacity="0.9"
                      />
                      
                      {/* End point indicator */}
                      {wireState.currentPosition.x && wireState.currentPosition.y && (
                        <circle
                          cx={wireState.currentPosition.x}
                          cy={wireState.currentPosition.y}
                          r="3"
                          fill="#ffffff"
                          stroke="#6b7280"
                          strokeWidth="2"
                          opacity="0.9"
                        />
                      )}
                    </svg>
                  )}

                  {/* Permanent wire connections */}
                  {wireConnections.length > 0 && (
                    <svg 
                      className="absolute inset-0 pointer-events-none" 
                      style={{ 
                        zIndex: 500,
                        width: '100%',
                        height: '100%'
                      }}
                    >
                      <defs>
                        <marker
                          id="permanent-arrowhead"
                          markerWidth="10"
                          markerHeight="7"
                          refX="9"
                          refY="3.5"
                          orient="auto"
                          fill="#6b7280"
                        >
                          <polygon points="0 0, 10 3.5, 0 7" />
                        </marker>
                      </defs>
                      
                      {wireConnections.map((connection) => {
                        const fromPos = getNodePosition(connection.fromNodeId, 'outflow');
                        const toPos = getNodePosition(connection.toNodeId, 'inflow');
                        
                        return (
                          <g key={connection.id}>
                            {/* Connection line */}
                            <line
                              x1={fromPos.x}
                              y1={fromPos.y}
                              x2={toPos.x}
                              y2={toPos.y}
                              stroke="#6b7280"
                              strokeWidth="3"
                              opacity="0.8"
                            />
                            
                            {/* Connection points */}
                            <circle
                              cx={fromPos.x}
                              cy={fromPos.y}
                              r="3"
                              fill="#6b7280"
                              opacity="0.9"
                            />
                            <circle
                              cx={toPos.x}
                              cy={toPos.y}
                              r="3"
                              fill="#6b7280"
                              opacity="0.9"
                            />
                          </g>
                        );
                      })}
                    </svg>
                  )}

                  {/* Render nodes */}
                </div>
              </div>
            ) : (
              /* Execution History */
              <div className="flex-1 bg-gray-50 p-6">
                {executionSummary && (
                  /* Summary Stats */
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-2xl font-bold text-gray-900">
                        {executionSummary.total_executions}
                      </div>
                      <div className="text-sm text-gray-500">Total Runs</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-2xl font-bold text-green-600">
                        {executionSummary.successful_executions}
                      </div>
                      <div className="text-sm text-gray-500">Successful</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-2xl font-bold text-red-600">
                        {executionSummary.failed_executions}
                      </div>
                      <div className="text-sm text-gray-500">Failed</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="text-2xl font-bold text-blue-600">
                        {formatDuration(executionSummary.average_duration_ms)}
                      </div>
                      <div className="text-sm text-gray-500">Avg Duration</div>
                    </div>
                  </div>
                )}

                {/* Execution List */}
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-medium text-gray-900">Recent Executions</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {executions.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        No execution history yet.
                        <br />
                        Executions will appear here when the automation runs.
                      </div>
                    ) : (
                      executions.map((execution) => (
                        <div key={execution.id} className="p-4 hover:bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getStatusIcon(execution.execution_status)}
                              <div>
                                <div className="font-medium text-gray-900 capitalize">
                                  {execution.execution_status}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {formatRelativeTime(execution.started_at)}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <div className="text-sm text-gray-900">
                                  {execution.executed_actions}/{execution.total_actions} actions
                                </div>
                                {execution.completed_at && (
                                  <div className="text-sm text-gray-500">
                                    {formatDuration(
                                      new Date(execution.completed_at).getTime() - 
                                      new Date(execution.started_at).getTime()
                                    )}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => handleViewExecution(execution.id)}
                                className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-colors"
                              >
                                <Eye className="h-3 w-3" />
                                Details
                              </button>
                            </div>
                          </div>
                          {execution.error_message && (
                            <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                              {execution.error_message}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No automation selected
              </h3>
              <p className="text-gray-500">
                Choose an automation from the sidebar to view and edit its workflow.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button 
              onClick={() => setError(null)}
              className="ml-4 text-red-700 hover:text-red-900"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Create Automation Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Create New Automation</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Automation Name
              </label>
              <input
                type="text"
                value={automationTitle}
                onChange={(e) => setAutomationTitle(e.target.value)}
                placeholder="Enter automation name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && automationTitle.trim()) {
                    handleSaveAutomation();
                  }
                }}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleCloseModal}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAutomation}
                disabled={!automationTitle.trim() || loading}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg font-medium transition-colors"
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trigger Library Modal */}
      {isTriggerLibraryOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">Add Trigger</h2>
              <button
                onClick={() => setIsTriggerLibraryOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="grid gap-4">
              {availableTriggers.map((trigger) => (
                <div
                  key={trigger.id}
                  onClick={() => handleSelectTrigger(trigger)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${trigger.color}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{trigger.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">{trigger.name}</h3>
                      <p className="text-sm text-gray-600">{trigger.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Action Library Modal */}
      {isActionLibraryOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">Add Action</h2>
              <button
                onClick={() => setIsActionLibraryOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="grid gap-4">
              {availableActions.map((action) => (
                <div
                  key={action.id}
                  onClick={() => handleSelectAction(action)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${action.color}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{action.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">{action.name}</h3>
                      <p className="text-sm text-gray-600">{action.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Trigger Settings Modal */}
      {isTriggerSettingsOpen && selectedTrigger && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4" style={{ marginTop: '80px', alignSelf: 'flex-start' }}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Trigger Settings</h2>
              <button
                onClick={handleCloseTriggerSettings}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Booking Event Type
                </label>
                <select
                  value={selectedTrigger.config.eventType}
                  onChange={(e) => {
                    const newEventType = e.target.value;
                    setTriggers(prev => prev.map(trigger => 
                      trigger.id === selectedTriggerId 
                        ? { ...trigger, config: { ...trigger.config, eventType: newEventType } }
                        : trigger
                    ));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="created">Created</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="canceled">Canceled</option>
                  <option value="deleted">Deleted</option>
                  <option value="partially_paid">Partially Paid</option>
                  <option value="paid">Paid</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={selectedTrigger.config.uiLabel || ''}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setTriggers(prev => prev.map(trigger => 
                      trigger.id === selectedTriggerId 
                        ? { ...trigger, config: { ...trigger.config, uiLabel: newUiLabel } }
                        : trigger
                    ));
                  }}
                  placeholder="e.g. 'New Customer SMS', 'Send Reminder', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this trigger on the canvas
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCloseTriggerSettings}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => selectedTriggerId && handleDeleteTrigger(selectedTriggerId)}
                  className="px-4 py-2 text-red-700 bg-red-100 hover:bg-red-200 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Execution Details Modal */}
      {isExecutionDetailsOpen && selectedExecutionId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 h-5/6">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900">Execution Details</h2>
              <button
                onClick={handleCloseExecutionDetails}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 h-full overflow-y-auto">
              {loadingSteps ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-gray-600">Loading execution details...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Execution Overview */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Execution Overview</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">Status</div>
                        <div className="flex items-center gap-2 mt-1">
                          {getStatusIcon(executions.find(e => e.id === selectedExecutionId)?.execution_status || 'pending')}
                          <span className="font-medium capitalize">
                            {executions.find(e => e.id === selectedExecutionId)?.execution_status}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Started</div>
                        <div className="font-medium mt-1">
                          {executions.find(e => e.id === selectedExecutionId)?.started_at && 
                            new Date(executions.find(e => e.id === selectedExecutionId)!.started_at).toLocaleString()
                          }
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Duration</div>
                        <div className="font-medium mt-1">
                          {(() => {
                            const execution = executions.find(e => e.id === selectedExecutionId);
                            if (execution?.completed_at) {
                              return formatDuration(
                                new Date(execution.completed_at).getTime() - 
                                new Date(execution.started_at).getTime()
                              );
                            }
                            return 'Running...';
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Actions</div>
                        <div className="font-medium mt-1">
                          {executions.find(e => e.id === selectedExecutionId)?.executed_actions || 0}/
                          {executions.find(e => e.id === selectedExecutionId)?.total_actions || 0}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Booking Context Data */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Booking Information</h3>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      {(() => {
                        const execution = executions.find(e => e.id === selectedExecutionId);
                        const contextData = execution?.context_data;
                        const bookingData = contextData?.booking_data;

                        if (!bookingData) {
                          return (
                            <div className="text-gray-500 text-center py-4">
                              No booking data available for this execution
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-4">
                            {/* Basic Booking Info */}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              <div>
                                <div className="text-sm text-gray-500">Booking ID</div>
                                <div className="font-medium">{bookingData.booking_id || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Title</div>
                                <div className="font-medium">{bookingData.title || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Grand Total</div>
                                <div className="font-medium">${bookingData.grand_total || 0}</div>
                              </div>
                            </div>

                            {/* Customer Info */}
                            {bookingData.customer && (
                              <div>
                                <div className="text-sm font-medium text-gray-700 mb-2">Customer</div>
                                <div className="bg-gray-50 rounded p-3 grid grid-cols-2 gap-3">
                                  <div>
                                    <div className="text-xs text-gray-500">Name</div>
                                    <div className="text-sm">{bookingData.customer.full_name || 'N/A'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Email</div>
                                    <div className="text-sm">{bookingData.customer.email || 'N/A'}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Phone</div>
                                    <div className="text-sm">{bookingData.customer.phone || 'N/A'}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Schedule Info */}
                            {bookingData.schedule && (
                              <div>
                                <div className="text-sm font-medium text-gray-700 mb-2">Schedule</div>
                                <div className="bg-gray-50 rounded p-3 grid grid-cols-2 gap-3">
                                  <div>
                                    <div className="text-xs text-gray-500">Drop Off</div>
                                    <div className="text-sm">
                                      {bookingData.schedule.drop_off_date || 'N/A'} {bookingData.schedule.drop_off_time || ''}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500">Start</div>
                                    <div className="text-sm">
                                      {bookingData.schedule.start_date || 'N/A'} {bookingData.schedule.start_time || ''}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Services */}
                            {bookingData.services && bookingData.services.length > 0 && (
                              <div>
                                <div className="text-sm font-medium text-gray-700 mb-2">Services</div>
                                <div className="space-y-2">
                                  {bookingData.services.map((service: any, index: number) => (
                                    <div key={index} className="bg-gray-50 rounded p-3">
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <div className="font-medium text-sm">{service.name}</div>
                                          <div className="text-xs text-gray-500">
                                            {service.vehicle?.year} {service.vehicle?.make} {service.vehicle?.model}
                                          </div>
                                        </div>
                                        <div className="text-sm font-medium">${service.price}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Raw Context Data (collapsible) */}
                            <details className="border-t pt-4">
                              <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                                Raw Context Data (Advanced)
                              </summary>
                              <div className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-64">
                                <pre>{JSON.stringify(contextData, null, 2)}</pre>
                              </div>
                            </details>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Execution Steps */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Execution Steps</h3>
                    {executionSteps.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No execution steps recorded for this run.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {executionSteps.map((step, index) => (
                          <div key={step.id} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-sm font-medium flex items-center justify-center">
                                  {index + 1}
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {step.automation_actions_v2?.action_type || 'Unknown Action'}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {step.started_at && formatRelativeTime(step.started_at)}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {getStatusIcon(step.execution_status)}
                                <span className="text-sm font-medium capitalize">
                                  {step.execution_status}
                                </span>
                              </div>
                            </div>
                            
                            {step.error_message && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                                <strong>Error:</strong> {step.error_message}
                              </div>
                            )}
                            
                            {step.result_data && Object.keys(step.result_data).length > 0 && (
                              <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded">
                                <div className="text-sm font-medium text-gray-700 mb-1">Result Data:</div>
                                <pre className="text-xs text-gray-600 overflow-auto max-h-32">
                                  {JSON.stringify(step.result_data, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SMS Action Settings Modal */}
      {smsActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">SMS Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={handleDeleteActionFromSettings}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* SMS Message Input */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SMS Message
                </label>
                <textarea
                  ref={messageTextareaRef}
                  value={smsActionSettingsModal.message}
                  onChange={(e) => setSmsActionSettingsModal(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Enter the SMS message to send when this automation triggers..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                />
                
                {/* Variable Dropdown */}
                {(() => {
                  const availableVariables = getAvailableVariables(smsActionSettingsModal.actionId || '');
                  
                  if (availableVariables.length > 0) {
                    return (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Insert Variable:
                        </label>
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              insertVariable(e.target.value);
                              e.target.value = ''; // Reset selection
                            }
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-transparent"
                          defaultValue=""
                        >
                          <option value="">Choose a variable to insert...</option>
                          {availableVariables.map((variable) => (
                            <option key={variable.value} value={variable.value} title={variable.description}>
                              {variable.label} - {variable.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else {
                    return (
                      <p className="text-xs text-yellow-600 mt-1">
                        💡 Connect this action to a trigger (like "Booking Created") to see available variables you can insert
                      </p>
                    );
                  }
                })()}
                
                <p className="text-xs text-gray-500 mt-1">
                  Variables will be automatically replaced with actual booking data when the SMS is sent.
                </p>
              </div>

              {/* Recipient Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notify
                </label>
                <select
                  value={smsActionSettingsModal.recipient}
                  onChange={(e) => setSmsActionSettingsModal(prev => ({ ...prev, recipient: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Select who to notify...</option>
                  {getAvailableRecipients(smsActionSettingsModal.actionId || '').map((recipient) => (
                    <option key={recipient.value} value={recipient.value}>
                      {recipient.label}
                    </option>
                  ))}
                </select>
                
                {/* Custom phone number input - only show when "custom" is selected */}
                {smsActionSettingsModal.recipient === 'custom' && (
                  <input
                    type="tel"
                    value={smsActionSettingsModal.customPhone || ''}
                    onChange={(e) => setSmsActionSettingsModal(prev => ({ ...prev, customPhone: e.target.value }))}
                    placeholder="Enter custom phone number..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent mt-2"
                  />
                )}
                
                {getAvailableRecipients(smsActionSettingsModal.actionId || '').length === 1 && (
                  <p className="text-xs text-yellow-600 mt-1">
                    💡 Connect this action to a trigger (like "Booking Created") to see more notification options
                  </p>
                )}
              </div>

              {/* UI Label Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  UI Label (Optional)
                </label>
                <input
                  type="text"
                  value={(() => {
                    const currentAction = actions.find(action => action.id === smsActionSettingsModal.actionId);
                    return currentAction?.config?.uiLabel || '';
                  })()}
                  onChange={(e) => {
                    const newUiLabel = e.target.value;
                    setActions(prev => prev.map(action => 
                      action.id === smsActionSettingsModal.actionId
                        ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                        : action
                    ));
                  }}
                  placeholder="e.g. 'Welcome Message', 'Reminder SMS', etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a short label to help identify this action on the canvas
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setSmsActionSettingsModal({ isOpen: false, actionId: null, message: '', recipient: '', customPhone: '' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSmsMessage}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delay Action Settings Modal */}
      {delayActionSettingsModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Wait Action Settings</h3>
              <div className="flex items-center gap-2">
                {/* Delete button */}
                <button
                  onClick={() => {
                    if (delayActionSettingsModal.actionId) {
                      // Remove action from local state
                      setActions(prev => prev.filter(action => action.id !== delayActionSettingsModal.actionId));
                      
                      // Remove any wire connections involving this action
                      setWireConnections(prev => prev.filter(connection => 
                        connection.fromNodeId !== delayActionSettingsModal.actionId && 
                        connection.toNodeId !== delayActionSettingsModal.actionId
                      ));
                      
                      // Close modal
                      setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' });
                    }
                  }}
                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Delete Action"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                {/* Close button */}
                <button
                  onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Wait Type Selection */}
            <div className="space-y-4">
              {(() => {
                const isCreatedTrigger = delayActionSettingsModal.actionId ? isConnectedToCreatedTrigger(delayActionSettingsModal.actionId) : false;
                
                return (
                  <>
                    {/* Wait Type Dropdown - only show if connected to 'created' trigger */}
                    {isCreatedTrigger && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wait Type
                        </label>
                        <select
                          value={delayActionSettingsModal.waitType}
                          onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                            ...prev, 
                            waitType: e.target.value as 'duration' | 'reminder'
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="duration">Wait Duration</option>
                          <option value="reminder">Reminder</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Choose between a fixed wait duration or a scheduled reminder before appointment.
                        </p>
                      </div>
                    )}

                    {/* Duration Configuration - show for duration type or when not connected to created trigger */}
                    {(delayActionSettingsModal.waitType === 'duration' || !isCreatedTrigger) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wait Duration
                        </label>
                        <div className="flex gap-3">
                          <input
                            type="number"
                            min="1"
                            max="999"
                            value={delayActionSettingsModal.duration}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              duration: parseInt(e.target.value) || 1 
                            }))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          <select
                            value={delayActionSettingsModal.unit}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              unit: e.target.value as 'minutes' | 'hours' | 'days' 
                            }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                          </select>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          The automation will pause for this duration before continuing to the next action.
                        </p>
                      </div>
                    )}

                    {/* Reminder Configuration - show only for reminder type */}
                    {delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger && (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Notify Time Before
                          </label>
                          <div className="flex gap-3 items-center">
                            <input
                              type="number"
                              min="1"
                              max="168"
                              value={delayActionSettingsModal.reminderHoursBefore}
                              onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                                ...prev, 
                                reminderHoursBefore: parseInt(e.target.value) || 1 
                              }))}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <span className="text-sm text-gray-600">hours before</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Reference Time
                          </label>
                          <select
                            value={delayActionSettingsModal.reminderReference}
                            onChange={(e) => setDelayActionSettingsModal(prev => ({ 
                              ...prev, 
                              reminderReference: e.target.value as 'drop_off_datetime' | 'start_datetime'
                            }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="drop_off_datetime">Drop Off Date & Time</option>
                            <option value="start_datetime">Start Date & Time</option>
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Choose which appointment time to calculate the reminder from.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Preview */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="text-sm font-medium text-blue-900">Preview:</div>
                      <div className="text-sm text-blue-700">
                        {delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger ? (
                          <>
                            Send reminder {delayActionSettingsModal.reminderHoursBefore} hours before{' '}
                            {delayActionSettingsModal.reminderReference === 'drop_off_datetime' ? 'drop off' : 'start'} time
                            <br />
                            <span className="text-xs text-blue-600">
                              Example: For a 2 PM appointment, notification will be sent at{' '}
                              {delayActionSettingsModal.reminderHoursBefore === 24 ? '2 PM the day before' : 
                               delayActionSettingsModal.reminderHoursBefore >= 24 ? 
                               `${Math.floor(delayActionSettingsModal.reminderHoursBefore / 24)} days and ${delayActionSettingsModal.reminderHoursBefore % 24} hours before` :
                               `${2 - delayActionSettingsModal.reminderHoursBefore} ${delayActionSettingsModal.reminderHoursBefore === 1 ? 'PM' : delayActionSettingsModal.reminderHoursBefore === 2 ? 'PM' : 'AM'}`
                              }
                            </span>
                          </>
                        ) : (
                          <>
                            Wait for {delayActionSettingsModal.duration} {delayActionSettingsModal.unit}
                            {(() => {
                              const duration = delayActionSettingsModal.duration;
                              const unit = delayActionSettingsModal.unit;
                              let totalMinutes = 0;
                              
                              if (unit === 'minutes') totalMinutes = duration;
                              else if (unit === 'hours') totalMinutes = duration * 60;
                              else if (unit === 'days') totalMinutes = duration * 24 * 60;
                              
                              if (totalMinutes < 60) {
                                return ` (${totalMinutes} minutes)`;
                              } else if (totalMinutes < 1440) {
                                return ` (${Math.round(totalMinutes / 60 * 10) / 10} hours)`;
                              } else {
                                return ` (${Math.round(totalMinutes / 1440 * 10) / 10} days)`;
                              }
                            })()}
                          </>
                        )}
                      </div>
                    </div>

                    {/* UI Label Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        UI Label (Optional)
                      </label>
                      <input
                        type="text"
                        value={(() => {
                          const currentAction = actions.find(action => action.id === delayActionSettingsModal.actionId);
                          return currentAction?.config?.uiLabel || '';
                        })()}
                        onChange={(e) => {
                          const newUiLabel = e.target.value;
                          setActions(prev => prev.map(action => 
                            action.id === delayActionSettingsModal.actionId
                              ? { ...action, config: { ...action.config, uiLabel: newUiLabel } }
                              : action
                          ));
                        }}
                        placeholder={
                          delayActionSettingsModal.waitType === 'reminder' && isCreatedTrigger 
                            ? "e.g. 'Appointment Reminder', '24hr Reminder', etc."
                            : "e.g. 'Cool Down Period', '24hr Delay', etc."
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Add a short label to help identify this action on the canvas
                      </p>
                    </div>

                    {/* Info Box */}
                    {!isCreatedTrigger && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="text-sm text-yellow-800">
                          <strong>💡 Tip:</strong> Connect this wait action to a "Booking Created" trigger to enable the reminder option for scheduled notifications before appointments!
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2 mt-4">
              <button
                onClick={() => setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' })}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!delayActionSettingsModal.actionId) return;

                  // Prepare configuration based on wait type
                  const waitConfig: any = {
                    wait_type: delayActionSettingsModal.waitType
                  };

                  if (delayActionSettingsModal.waitType === 'duration') {
                    // Convert to minutes for storage
                    let durationInMinutes = delayActionSettingsModal.duration;
                    if (delayActionSettingsModal.unit === 'hours') {
                      durationInMinutes = delayActionSettingsModal.duration * 60;
                    } else if (delayActionSettingsModal.unit === 'days') {
                      durationInMinutes = delayActionSettingsModal.duration * 24 * 60;
                    }

                    waitConfig.duration_minutes = durationInMinutes;
                    waitConfig.duration_type = delayActionSettingsModal.unit;
                    waitConfig.duration = durationInMinutes; // Legacy field
                    waitConfig.unit = delayActionSettingsModal.unit; // Legacy field
                    waitConfig.displayDuration = delayActionSettingsModal.duration; // Legacy field
                  } else if (delayActionSettingsModal.waitType === 'reminder') {
                    waitConfig.reminder_hours_before = delayActionSettingsModal.reminderHoursBefore;
                    waitConfig.reminder_reference = delayActionSettingsModal.reminderReference;
                  }

                  // Update the action with the new configuration
                  setActions(prev => prev.map(action => 
                    action.id === delayActionSettingsModal.actionId
                      ? { 
                          ...action, 
                          config: { 
                            ...action.config, 
                            ...waitConfig
                          }
                        }
                      : action
                  ));

                  // Close modal
                  setDelayActionSettingsModal({ isOpen: false, actionId: null, waitType: 'duration', duration: 1, unit: 'hours', reminderHoursBefore: 24, reminderReference: 'drop_off_datetime' });
                }}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Title Modal */}
      {isEditTitleModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Edit Automation Title</h2>
              <button
                onClick={() => setIsEditTitleModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Automation Title
              </label>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Enter automation title..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && editTitle.trim()) {
                    handleSaveTitle();
                  }
                }}
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsEditTitleModalOpen(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTitle}
                disabled={!editTitle.trim() || loading}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg font-medium transition-colors"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Automation Modal */}
      {isDeleteAutomationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Delete Automation</h2>
              <button
                onClick={() => setIsDeleteAutomationModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Are you sure?</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                This will permanently delete the automation "{selectedAutomation?.name}" and all of its:
              </p>
              <ul className="text-sm text-gray-600 mt-2 ml-4 list-disc">
                <li>Triggers and actions</li>
                <li>Connections and workflow</li>
                <li>Execution history</li>
                <li>Scheduled jobs</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setIsDeleteAutomationModalOpen(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Automation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationsV2; 